import React, { useState } from 'react';
import BusinessObjectives from '../components/BusinessObjectives';
import ContactPopup from '../components/ContactPopup';
import { CheckCircle2, Award, Users, Clock } from 'lucide-react';

const About: React.FC = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <>
    <main>
        {/* Hero Section */}
        <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center z-0"
            style={{ 
              backgroundImage: `url('https://images.pexels.com/photos/1643384/pexels-photo-1643384.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
              backgroundPosition: 'center center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 to-primary-900/50"></div>
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 relative z-10 text-white animate-fade-in">
            <div className="max-w-3xl">
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                About SL Rooms
              </h1>
              <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl">
                Transforming commercial spaces into quality residential units across Southeast England
              </p>
              <div className="flex space-x-4 mb-12">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsContactOpen(true);
                  }}
                  className="px-6 py-3 bg-accent-400 hover:bg-accent-500 text-primary-950 font-medium rounded-md transition-colors duration-300 inline-flex items-center"
                >
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About Content */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-primary-900">
                  Our Mission
                </h2>
                <p className="text-gray-600 mb-6">
                  SL Rooms Limited specialises in converting commercial and mixed-use properties into 
                  residential units through Planning Approval or Permitted Development Rights. Our vision 
                  is to create 100 residential units within 5 years, helping address the UK's housing shortage.
                </p>
                <p className="text-gray-600 mb-8">
                  Our team of experienced professionals ensures each project meets the highest standards 
                  of sustainability and energy efficiency, delivered within timeframe and budget. We focus 
                  on creating affordable 1-2 bedroom apartments with 10-year guarantees, providing buyers 
                  with safe, quality homes built to last decades.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-primary-700 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Strategic Locations</h3>
                      <p className="text-sm text-gray-600">Close to transport links and schools in Southeast England</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-primary-700 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Quality Assurance</h3>
                      <p className="text-sm text-gray-600">10-year guarantee on all converted properties</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-primary-700 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Sustainable Development</h3>
                      <p className="text-sm text-gray-600">Focus on energy efficiency and sustainability</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-primary-700 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Affordable Housing</h3>
                      <p className="text-sm text-gray-600">Creating quality 1-2 bedroom apartments at accessible prices</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="relative z-10 rounded-lg overflow-hidden shadow-xl">
                  <img
                    src="https://images.pexels.com/photos/3935350/pexels-photo-3935350.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                    alt="SL Rooms project"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="absolute inset-0 transform translate-x-4 translate-y-4 bg-primary-200 rounded-lg -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Business Objectives Section */}
        <BusinessObjectives />

        {/* Values Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-primary-900">
                Our Values
              </h2>
              <p className="text-gray-600">
                Our core values guide every decision we make and every project we undertake.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto">
                  <Award className="h-8 w-8 text-primary-800" />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3 text-center text-gray-900">
                  Excellence
                </h3>
                <p className="text-gray-600 text-center">
                  We maintain the highest standards in property development, from initial planning to final delivery.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto">
                  <Users className="h-8 w-8 text-primary-800" />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3 text-center text-gray-900">
                  Sustainability
                </h3>
                <p className="text-gray-600 text-center">
                  We prioritise energy efficiency and sustainable practices in all our developments.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto">
                  <Clock className="h-8 w-8 text-primary-800" />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3 text-center text-gray-900">
                  Reliability
                </h3>
                <p className="text-gray-600 text-center">
                  We deliver projects on time and within budget, maintaining transparency throughout.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    <ContactPopup isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
};

export default About;